var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useEffect } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import styled from '../../styled';
const TextEditorWrapper = styled.div `
  position: relative;
  height: 100%;
  min-height: 11.5rem;
  padding: 0.5rem 0.3rem 1.75rem 0.3rem;
  border-radius: 6px;
  border: solid 0.0625rem
    ${({ theme, status }) => status === 'invalid' ? theme.colors.RED_600 : theme.colors.INK_300};
  background-color: ${({ theme }) => theme.colors.SNOW_100};
  cursor: ${({ readOnly }) => (readOnly ? 'not-allowed' : 'unset')};

  /* override draft default style */
  * {
    box-sizing: unset;
  }
  .public-DraftStyleDefault-block {
    margin: 0px !important;
    line-height: 1.5;
  }
  .public-DraftEditorPlaceholder-inner {
    color: ${({ theme }) => theme.colors.INK_400};
    font-weight: normal;
  }
  .rdw-option-wrapper,
  .rdw-dropdown-wrapper {
    border: solid 0.0625rem ${({ theme }) => theme.colors.INK_300};
    border-radius: 2px;
    box-shadow: unset;
    &:hover {
      box-shadow: unset;
      background-color: #fdfdfd;
    }
  }
  .rdw-option-active {
    background-color: #f6f9ff;
    box-shadow: unset;
  }
  .rdw-dropdown-optionwrapper {
    &:hover {
      box-shadow: unset;
    }
  }
  .rdw-link-modal,
  .rdw-colorpicker-modal {
    color: ${({ theme }) => theme.colors.INK_800};
    box-shadow: 0 0.125rem 0.25rem 0 ${({ theme }) => theme.colors.SHADOW_DARK},
      0 0.125rem 0.25rem 0 ${({ theme }) => theme.colors.SHADOW_DEFAULT};
    border-radius: 6px;
    font-size: 0.875rem;
    height: unset;
  }
  .rdw-link-modal-input {
    border-radius: 6px;
    height: unset;
    padding: 0.5rem;
    border: solid 0.0625rem ${({ theme }) => theme.colors.INK_300};
    &:focus,
    &:active {
      border-color: ${({ theme }) => theme.colors.PRIMARY_600};
    }
  }
  .rdw-link-modal-buttonsection {
    display: flex;
    flex-flow: row-reverse;
    align-items: flex-start;
    margin: 0px;
  }
  .rdw-link-modal-btn {
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 6px;
    height: 2.125rem;
    margin-bottom: 0px;
    border: unset;
    box-shadow: 0 0.0625rem 0.125rem 0.0625rem
      ${({ theme }) => theme.colors.SHADOW_DEFAULT};
    &:hover {
      box-shadow: 0 0.0625rem 0.125rem 0.0625rem
        ${({ theme }) => theme.colors.SHADOW_DEFAULT};
    }
    &:last-child {
      &:hover {
        color: ${({ theme }) => theme.colors.PRIMARY_500};
      }
    }
    &:first-child {
      color: ${({ theme }) => theme.colors.SNOW_200};
      background-color: ${({ theme }) => theme.colors.PRIMARY_600};
      &:disabled {
        box-shadow: unset;
        background-color: ${({ theme }) => theme.colors.PRIMARY_300};
      }
      &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.colors.PRIMARY_500};
      }
    }
  }
`;
const TextCounter = styled.div `
  position: absolute;
  right: 0.5rem;
  bottom: 0.375rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.INK_500};
`;
const ErrorMessage = styled.div `
  font-size: 0.8125rem;
  padding-top: 0.25rem;
  color: ${({ theme }) => theme.colors.RED_600};
`;
const EditorInfo = styled.div `
  font-size: 0.8125rem;
  padding-top: 0.25rem;
  color: ${({ theme }) => theme.colors.INK_500};
`;
const toolbarStyle = {
    border: 'none',
    padding: '0',
};
const editorStyle = {
    padding: '0 0.3rem',
};
/* docs: https://jpuri.github.io/react-draft-wysiwyg/#/docs */
const defaultConfig = {
    options: ['inline', 'fontSize', 'colorPicker', 'link'],
    inline: {
        options: ['bold', 'italic', 'underline'],
    },
    link: {
        options: ['link'],
    },
};
export const initEditorState = (content) => {
    if (content) {
        const blocksFromHtml = htmlToDraft(content);
        const { contentBlocks, entityMap } = blocksFromHtml;
        const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
        return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
};
export const convertEditStateToHtml = (editorState) => {
    return draftToHtml(convertToRaw(editorState.getCurrentContent()));
};
export const convertEditStateToText = (editorState) => {
    return editorState.getCurrentContent().getPlainText();
};
export const TextEditor = (_a) => {
    var { onChange, editorState, status, errorMessage, editorInfo, maxLength, placeholder, readOnly, customConfig, onBlur, shouldAutoFocus = true } = _a, restProps = __rest(_a, ["onChange", "editorState", "status", "errorMessage", "editorInfo", "maxLength", "placeholder", "readOnly", "customConfig", "onBlur", "shouldAutoFocus"]);
    const textCount = editorState.getCurrentContent().getPlainText().length;
    const editorElement = useRef();
    const setEditorReference = (ref) => {
        editorElement.current = ref;
    };
    const focusEditor = () => {
        if (editorElement.current) {
            editorElement.current.focus();
        }
    };
    useEffect(() => {
        if (shouldAutoFocus) {
            focusEditor();
        }
    }, []);
    const handleInput = () => {
        if (maxLength && textCount >= maxLength) {
            return 'handled';
        }
        return null;
    };
    const handlePastedText = (text) => !!(maxLength && text.length + textCount > maxLength);
    const editor = (
    // @ts-ignore
    React.createElement(Editor, { editorRef: setEditorReference, editorState: editorState, onEditorStateChange: onChange, 
        // @ts-ignore
        handleReturn: handleInput, handleBeforeInput: handleInput, handlePastedText: handlePastedText, toolbar: customConfig || defaultConfig, editorStyle: editorStyle, toolbarStyle: toolbarStyle, toolbarHidden: readOnly, placeholder: placeholder, readOnly: readOnly, onBlur: onBlur }));
    return (React.createElement("div", Object.assign({}, restProps),
        React.createElement(TextEditorWrapper, { status: status, readOnly: readOnly, onClick: focusEditor },
            editor,
            maxLength && (React.createElement(TextCounter, null,
                textCount,
                "/",
                maxLength))),
        errorMessage && React.createElement(ErrorMessage, null, errorMessage),
        editorInfo && React.createElement(EditorInfo, null, editorInfo)));
};
//# sourceMappingURL=index.js.map